import React, { useContext, useEffect, useState } from "react";
import ReactTable from "react-table";
import ReactTablePagination from "../../task-list/components/pagination";
import { getMeasurementData, getWipMeasurements, deleteMeasurements } from "../../measuring/lib/measuring-data";
import { useMeasurementStatus } from "./hooks/use-measurement-status";
import * as notify from "../../../shared/notify";
import eventEmitter from "../../../shared/events";
import { columns } from "./constants";
import Notes from "components/notes/contexts/notes";
import NotePopOut from "components/notes/note-popout";
import { setDefaultPosition } from "components/measuring/lib/helpers";
import { ShipmentContext } from "../contexts";
import { useMeasurements } from "./hooks/use-measurements";
import "../../measuring/measuring.scss";

const MeasurementsTable = ({ disableNotes, setDisableSave, disableCompleteRedo, ...props }) => {
    const { upcTasks, refreshUpcs, isInDuplicateStaging } = useContext(ShipmentContext);
    const {
        selectedUpcs,
        setSelectedUpcs,
        updateSelectedUpcs,
        setSingleHighlightedMeasuringWipKey,
        setSingleHighlightedCodingWipKey,
        setMeasurementState,
        measurementState,
    } = useMeasurements();
    const { mutate: getMeasurementStatus } = useMeasurementStatus();
    const [products, setProducts] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    const [upcsWithMeasurements, getUpcsWithMeasurements] = useState([]);
    const [selectedKey, setSelectedKey] = useState("");
    const [noteSettings, setNoteSettings] = useState(null);
    const [showNotes, setShowNotes] = useState(false);

    useEffect(() => {
        eventEmitter.on("UPDATE_MEASURING_NOTE_SETTINGS", updateNoteSettings);
        eventEmitter.on("UPDATE_SLA", refreshUpcs);

        return () => {
            eventEmitter.removeListener("UPDATE_MEASURING_NOTE_SETTINGS", updateNoteSettings);
            eventEmitter.removeListener("UPDATE_SLA", refreshUpcs);
        };
    }, []);

    useEffect(() => {
        loadProducts();
    }, [upcTasks]);

    useEffect(() => {
        getInitialMeasurements().then((result) => {
            getUpcsWithMeasurements(result);
        });
    }, [products]);

    useEffect(() => {
        eventEmitter.on("REFRESH_TASK_STATUS", loadProducts);

        return () => {
            eventEmitter.removeListener("REFRESH_TASK_STATUS", loadProducts);
        };
    }, [upcsWithMeasurements]);

    useEffect(() => {
        if (selectedUpcs.length > 0) {
            setMeasurementState((current) => ({
                ...current,
                measurements: [],
                isCompleted: false,
                measuringWipKey: null,
                positionList: setDefaultPosition(measurementState.fullPositionList),
            }));
        }

        if (upcsWithMeasurements) {
            upcsWithMeasurements.map((upc) => {
                if (selectedUpcs.some((select) => select.upc === upc.upc && select.productModifier === upc.productModifier)) {
                    return notifyDeleteMeasurements();
                }

                return null;
            });
        }
    }, [selectedUpcs]);

    const getInitialMeasurements = () => {
        return getMeasurementData(props.shipmentKey);
    };

    const loadProducts = () => {
        const taskContextKeys = upcTasks.map((product) => product.key);

        if (taskContextKeys.length > 0) {
            handleLoadUpcStatuses(taskContextKeys);
        }
    };

    const handleLoadUpcStatuses = (taskContextKeys) => {
        getMeasurementStatus(
            { taskContextKeys, isInDuplicateStaging },
            {
                onSuccess: (tasks) => {
                    setProducts(tasks);
                },
            }
        );
    };

    const notifyDeleteMeasurements = async () => {
        let error = false;

        products.forEach((item) => {
            if (
                selectedUpcs.some((select) => select.upc === item.context.upc && select.productModifier === item.context.productModifier) &&
                item.state === "completed"
            ) {
                setSelectedUpcs([]);
                setDisableSave(false);

                return (error = true);
            }
        });

        if (error) {
            return notify.error("Cannot edit measurements for completed UPC");
        } else {
            const verify = await notify.confirm(
                "One or more UPC(s) already have measurements. Performing a bulk edit will reset these measurements."
            );

            if (verify) {
                eventEmitter.emit("REFRESH_TASK_STATUS");
                const wipKeys = [];
                upcsWithMeasurements.map((item) => {
                    if (selectedUpcs.some((select) => select.upc === item.upc && select.productModifier === item.productModifier)) {
                        wipKeys.push(item.measuringWipKey);
                    }

                    return null;
                });

                await deleteMeasurements(wipKeys);
            } else {
                setSelectedUpcs([]);
                setDisableSave(false);
            }
        }
    };

    const toggleNotes = (bool) => {
        const useBool = bool ? bool : !showNotes;
        setShowNotes(useBool);
    };

    const updateNoteSettings = (newSettings) => {
        setNoteSettings({ ...newSettings });
        setShowNotes(true);
    };

    const onRowClick = (state, rowInfo) => {
        return {
            onClick: async () => {
                if (!rowInfo) {
                    return;
                }

                rowInfo.original.state === "redo" && disableCompleteRedo ? setDisableSave(true) : setDisableSave(false);

                setSelectedKey(rowInfo.original.key);
                setSelectedUpcs([]);
                const measurements = await getWipMeasurements(rowInfo.original.context.measuringWipKey);

                setSingleHighlightedCodingWipKey(rowInfo.original.context.codingWipKey);
                setSingleHighlightedMeasuringWipKey(rowInfo.original.state === "redo" && rowInfo.original.context.measuringWipKey);

                setMeasurementState((current) => ({
                    ...current,
                    measurements: measurements,
                    measuringWipKey: rowInfo.original.context.measuringWipKey,
                    isCompleted: rowInfo.original.state === "completed",
                    positionList: setDefaultPosition(measurementState.fullPositionList, measurements),
                }));
            },
            style: {
                cursor: "pointer",
                backgroundColor:
                    selectedKey && selectedUpcs.length <= 0 && rowInfo && rowInfo.original && rowInfo.original.key === selectedKey
                        ? "#f9de473e"
                        : "#ffffff",
            },
        };
    };

    const onPageSizeChange = (pSize) => {
        setPageSize(pSize);
    };

    return (
        <>
            <h3>Measurement Details</h3>
            <ReactTable
                className="-striped"
                data={products}
                columns={columns(props.shipmentKey, products, selectedUpcs, setSelectedUpcs, updateSelectedUpcs, upcsWithMeasurements)}
                PaginationComponent={ReactTablePagination}
                minRows="5"
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                shipmentKey={props.shipmentKey}
                getTdProps={onRowClick}
            />
            {showNotes && (
                <div>
                    <Notes
                        settings={noteSettings.settings}
                        clientCode={noteSettings.clientCode}
                        binTrackingNumber={noteSettings.binTrackingNumber}
                        noteType="Product"
                        taskType="measuring.standard"
                        sourcePage="shipment"
                    >
                        <NotePopOut
                            upc={noteSettings.upc}
                            toggleNotes={toggleNotes}
                            taskType="measuring.standard"
                            isReadOnly={disableNotes}
                        />
                    </Notes>
                </div>
            )}
        </>
    );
};

export default MeasurementsTable;
